import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, Pencil, X } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api.js';

/** Id of the lead's current vendor, whether the API sent an id or a populated {_id, name}. */
export const vendorIdOf = (lead) => String(lead?.vendor?._id || lead?.vendor || '');
export const vendorNameOf = (lead) => lead?.vendor?.name || lead?.leadVendorName || '';

/**
 * "Lead Vendor" line with an inline editor: pick another vendor from the tenant's vendor list.
 * @param {{lead: object, leadId: string, onChange?: () => void}} props
 */
export default function VendorRow({ lead, leadId, onChange }) {
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['lead-vendors-list'],
    queryFn: () => apiGet('/lead-vendors'),
    enabled: editing,
    staleTime: 60_000,
  });
  const vendors = (data?.vendors || []).filter((v) => !v.archived);

  function begin() { setChoice(vendorIdOf(lead)); setEditing(true); }

  async function save() {
    if (!choice || choice === vendorIdOf(lead)) { setEditing(false); return; }
    setSaving(true);
    try {
      await apiPut(`/leads/${leadId}`, { vendor: choice });
      toast.success('Lead vendor updated');
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update the vendor');
    }
    setSaving(false);
  }

  return (
    <div className="py-0.5" data-testid="vendor-row">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-white/40">Lead Vendor</span>
        <span className="flex items-center gap-1.5">
          <span className="max-w-[150px] truncate text-[11px] text-white/70">{vendorNameOf(lead) || '—'}</span>
          {!editing && (
            <button type="button" onClick={begin} aria-label="Edit lead vendor" className="text-civix-purpleSoft hover:text-white" data-testid="edit-vendor"><Pencil className="h-3 w-3" /></button>
          )}
        </span>
      </div>
      {editing && (
        <div className="mt-1.5 rounded-lg border border-civix-border bg-civix-panel p-2" data-testid="vendor-editor">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-white/40">Set lead vendor</div>
          <div className="flex items-center gap-1.5">
            <select value={choice} onChange={(e) => setChoice(e.target.value)} disabled={isLoading} aria-label="Lead vendor" data-testid="vendor-select" className="min-w-0 flex-1 rounded-md border border-civix-purple/50 bg-civix-bg px-2 py-1.5 text-xs text-white">
              {isLoading && <option value="">Loading vendors…</option>}
              {!isLoading && vendors.length === 0 && <option value="">No vendors yet</option>}
              {vendors.map((v) => <option key={v._id} value={v._id} className="text-black">{v.name}</option>)}
            </select>
            <button type="button" onClick={save} disabled={saving || !choice} aria-label="Save vendor" data-testid="vendor-save" className="rounded p-1.5 text-emerald-400 hover:bg-white/5 disabled:opacity-40"><Check className="h-4 w-4" /></button>
            <button type="button" onClick={() => setEditing(false)} aria-label="Cancel" data-testid="vendor-cancel" className="rounded p-1.5 text-rose-400 hover:bg-white/5"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
